import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { ChangeCircleOutlined, Done } from "@mui/icons-material";
import { SchoolClassSelectDialog } from "../../features/school/components/SchoolClassSelectDialog";

const DetailsStack = ({
  details,
  handleChange,
  possibleSubjects = [],
  quiz = false,
}) => {
  return (
    <>
      <Stack spacing={3} sx={{ mt: 2, mb: 2 }} direction={"row"}>
        <TextField
          sx={{
            width: "30%",
            "& .MuiOutlinedInput-root": {
              height: 52,
            },
          }}
          label={quiz ? "Quiz Name" : "Assignment Name"}
          variant="outlined"
          value={details.name ?? ""}
          onChange={(e) => handleChange("name", e.target.value)}
        />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            key={details.dueDate?.toString() || "empty"} // forces remount on date change
            format="DD/MM/YYYY"
            label="Due Date"
            value={details.dueDate ?? null}
            minDate={dayjs()}
            onChange={(newValue) => handleChange("dueDate", newValue)}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 56,
              },
            }}
          />
        </LocalizationProvider>

        {!quiz && possibleSubjects?.length > 0 && (
          <FormControl fullWidth>
            <InputLabel id="subject-label">Subject</InputLabel>
            <Select
              labelId="subject-label"
              label="Subject"
              value={details.subject?.id || ""}
              onChange={(e) => {
                const selectedObj = possibleSubjects.find(
                  (sub) => sub.id === e.target.value,
                );
                handleChange("subject", selectedObj);
              }}
              // Custom rendering for the selected display box
              renderValue={(selectedId) => {
                const selectedItem = possibleSubjects.find(
                  (s) => s.id === selectedId,
                );

                // Check if ANY OTHER subject has frameworks
                const othersHaveFrameworks = possibleSubjects.some(
                  (sub) =>
                    sub.id !== selectedId && sub.feedbackFrameworks?.length > 0,
                );

                return (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    justifyContent={"space-between"}
                  >
                    <Box>{selectedItem?.name}</Box>
                    {othersHaveFrameworks && (
                      <Tooltip
                        title="Related subjects with frameworks are available"
                        arrow
                      >
                        <Chip
                          label="Related frameworks available"
                          color={"text.primary"}
                          icon={
                            <Box sx={{ fontWeight: "bold", px: 0.5 }}>!</Box>
                          } // Simple text icon
                          sx={{
                            ml: 1,
                            cursor: "help",
                            border: 0,
                            backgroundColor: "warning.light",
                          }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                );
              }}
            >
              {possibleSubjects.map((sub) => (
                <MenuItem key={sub.id} value={sub.id}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                  >
                    <Box>{sub.name}</Box>

                    {sub.feedbackFrameworks?.length > 0 && (
                      <Tooltip
                        title="School Feedback Framework Available"
                        arrow
                      >
                        <Chip
                          label={`Frameworks: ${sub.feedbackFrameworks
                            .map((fw) => fw.educationLevel.level)
                            .join(", ")}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>
      {quiz && (
        <Stack spacing={2} sx={{ mt: 2, mb: 3 }} direction={"column"}>
          <TextField
            fullWidth
            label="Quick description"
            variant="outlined"
            value={details.description ?? ""}
            onChange={(e) => handleChange("description", e.target.value)}
          />
        </Stack>
      )}
    </>
  );
};

export const AddDetails = ({
  details,
  onChange,
  schoolClass,
  onSchoolClassChange,
  onComplete = () => {},
  canBePublished,
  quiz = false,
  renderAdditionalActions,
}) => {
  const theme = useTheme();
  const [schoolSelectOpen, setSchoolSelectOpen] = useState(false);

  useEffect(() => {
    if (!schoolClass) {
      setSchoolSelectOpen(true);
    } else {
      setSchoolSelectOpen(false);
    }
  }, [schoolClass]);

  const handleChangeSchoolClicked = () => {
    onSchoolClassChange(null);
    setSchoolSelectOpen(true);
  };

  const handleChange = (field, value) => {
    onChange({ ...details, [field]: value });
  };

  return (
    <>
      <SchoolClassSelectDialog
        open={schoolSelectOpen}
        setOpen={setSchoolSelectOpen}
        onSelect={onSchoolClassChange}
        quiz={quiz}
        renderAlternativeAction={renderAdditionalActions}
      />
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        sx={{ flexWrap: "wrap", gap: 2 }}
      >
        <Typography variant="h3">
          Add {quiz ? "quiz" : "assignment"} details
        </Typography>
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
          {renderAdditionalActions?.()}
          <Button
            disabled={!canBePublished}
            onClick={onComplete}
            endIcon={<Done />}
            variant={"contained"}
          >
            Finish
          </Button>
        </Stack>
      </Stack>
      {schoolClass ? (
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Creating {quiz ? "quiz" : "assignment"} for class:{" "}
          <strong>{schoolClass.name}</strong>{" "}
          <IconButton
            onClick={handleChangeSchoolClicked}
            sx={{
              border: 0,
              color: (theme.vars || theme).palette.primary.main,
            }}
          >
            <ChangeCircleOutlined />
          </IconButton>
        </Typography>
      ) : (
        <Button
          variant={"contained"}
          onClick={handleChangeSchoolClicked}
          sx={{ mt: 2 }}
        >
          Choose Class
        </Button>
      )}
      <DetailsStack
        quiz={quiz}
        details={details}
        possibleSubjects={schoolClass?.subject?.subjectAndChildren || []}
        handleChange={handleChange}
      />
    </>
  );
};

export { DetailsStack };
